import { ConsoleCommandDefinition, ConsoleCommandName } from './types';
import { WebsiteStatus } from '@/services/Website/interfaces';

export const CONSOLE_STATUS_VALUES: string[] = [
  WebsiteStatus.ACTIVE,
  WebsiteStatus.PENDING,
  WebsiteStatus.DISABLED,
  WebsiteStatus.ERROR,
];

export const CONSOLE_FILTER_KEYS = ['status', 'server', 'owner', 'page', 'limit', 'domain'] as const;

export const CONSOLE_LOG_TYPES = ['access', 'error'] as const;

export const CONSOLE_META_COMMANDS = ['help', 'clear', 'history', 'rerun'];

export const CONSOLE_OPEN_TARGETS = ['server', 'group', 'unix'] as const;

export const CONSOLE_ALIASES: { [alias: string]: string } = {
  errors: '/sites status:ERROR',
  disabled: '/sites status:DISABLED',
  pending: '/sites status:PENDING',
};

export const CONSOLE_COMMANDS: ConsoleCommandDefinition[] = [
  {
    name: 'help',
    description: 'Show available commands and examples',
    usage: '/help',
    examples: ['/help'],
  },
  {
    name: 'find',
    description: 'Search websites by domain or open by numeric ID',
    usage: '/find <domain-or-id>',
    examples: ['/find example.com', '/find 12345'],
    requiresOperand: true,
  },
  {
    name: 'sites',
    description: 'List websites with optional filters',
    usage: '/sites [status:STATUS] [server:name] [owner:id] [domain:text] [page:n] [limit:n]',
    examples: [
      '/sites status:ERROR',
      '/sites server:web01 limit:25',
      '/sites domain:example page:1',
    ],
  },
  {
    name: 'errors',
    description: 'Shortcut for /sites status:ERROR',
    usage: '/errors',
    examples: ['/errors'],
    aliasOf: '/sites status:ERROR',
  },
  {
    name: 'disabled',
    description: 'Shortcut for /sites status:DISABLED',
    usage: '/disabled',
    examples: ['/disabled'],
    aliasOf: '/sites status:DISABLED',
  },
  {
    name: 'pending',
    description: 'Shortcut for /sites status:PENDING',
    usage: '/pending',
    examples: ['/pending'],
    aliasOf: '/sites status:PENDING',
  },
  {
    name: 'use',
    description: 'Set, show, or clear the current website context',
    usage: '/use [domain-or-id|clear]',
    examples: ['/use example.com', '/use', '/use clear'],
    optionalOperand: true,
  },
  {
    name: 'site',
    description: 'Show a single website summary (uses context when omitted)',
    usage: '/site [domain-or-id]',
    examples: ['/site example.com', '/site'],
    optionalOperand: true,
  },
  {
    name: 'activity',
    description: 'Show orchestrator activity (uses context when omitted)',
    usage: '/activity [domain-or-id]',
    examples: ['/activity example.com', '/activity'],
    optionalOperand: true,
  },
  {
    name: 'open',
    description: 'Navigate to website, server, group, or unix user (uses context when omitted)',
    usage: '/open [domain-or-id|server|group|unix] [domain-or-id]',
    examples: [
      '/open example.com',
      '/open',
      '/open server',
      '/open group',
      '/open unix',
    ],
    optionalOperand: true,
  },
  {
    name: 'health',
    description: 'Combined status, DNS, SSL, and orchestrator snapshot',
    usage: '/health [domain-or-id]',
    examples: ['/health example.com', '/health'],
    optionalOperand: true,
  },
  {
    name: 'dns',
    description: 'DNS / HTTP probe, or manage DNS records',
    usage: '/dns [manage] [domain-or-id]',
    examples: ['/dns example.com', '/dns', '/dns manage', '/dns manage example.com'],
    optionalOperand: true,
  },
  {
    name: 'ssl',
    description: 'SSL certificate summary, or manage certificates',
    usage: '/ssl [manage] [domain-or-id]',
    examples: ['/ssl example.com', '/ssl', '/ssl manage', '/ssl manage example.com'],
    optionalOperand: true,
  },
  {
    name: 'logs',
    description: 'Request access or error logs (delivered over WebSocket)',
    usage: '/logs [domain-or-id] [access|error] [lines]',
    examples: ['/logs example.com error 100', '/logs error 50'],
    optionalOperand: true,
  },
  {
    name: 'actions',
    description: 'Show available actions for the current context',
    usage: '/actions [domain-or-id]',
    examples: ['/actions', '/actions example.com'],
    optionalOperand: true,
  },
  {
    name: 'staging',
    description: 'Create or merge a staging environment',
    usage: '/staging <create|merge> [domain-or-id]',
    examples: ['/staging create', '/staging merge', '/staging create example.com'],
    requiresOperand: true,
  },
  {
    name: 'alias',
    description: 'Create a domain alias',
    usage: '/alias create [domain-or-id]',
    examples: ['/alias create', '/alias create example.com'],
    requiresOperand: true,
  },
  {
    name: 'subdomain',
    description: 'Create a subdomain website',
    usage: '/subdomain create [domain-or-id]',
    examples: ['/subdomain create', '/subdomain create example.com'],
    requiresOperand: true,
  },
  {
    name: 'wp',
    description: 'Install or uninstall WordPress',
    usage: '/wp <install|uninstall> [domain-or-id]',
    examples: ['/wp install', '/wp uninstall', '/wp install example.com'],
    requiresOperand: true,
  },
  {
    name: 'enable',
    description: 'Enable a website (set status ACTIVE)',
    usage: '/enable [domain-or-id]',
    examples: ['/enable', '/enable example.com'],
    optionalOperand: true,
  },
  {
    name: 'disable',
    description: 'Disable a website (set status DISABLED)',
    usage: '/disable [domain-or-id]',
    examples: ['/disable', '/disable example.com'],
    optionalOperand: true,
  },
  {
    name: 'rename',
    description: 'Rename the group main website domain',
    usage: '/rename [domain-or-id]',
    examples: ['/rename', '/rename example.com'],
    optionalOperand: true,
  },
  {
    name: 'delete',
    description: 'Delete a website',
    usage: '/delete [domain-or-id]',
    examples: ['/delete', '/delete example.com'],
    optionalOperand: true,
  },
  {
    name: 'recreate',
    description: 'Retry failed website creation',
    usage: '/recreate [domain-or-id]',
    examples: ['/recreate', '/recreate example.com'],
    optionalOperand: true,
  },
  {
    name: 'retry',
    description: 'Retry a failed creation action',
    usage: '/retry [creation-id] [action-id] [domain-or-id]',
    examples: ['/retry', '/retry 12 34', '/retry 12 34 example.com'],
    optionalOperand: true,
  },
  {
    name: 'db',
    description: 'Manage website databases',
    usage: '/db manage [domain-or-id]',
    examples: ['/db manage', '/db manage example.com'],
    requiresOperand: true,
  },
  {
    name: 'mail',
    description: 'Manage website mailboxes',
    usage: '/mail manage [domain-or-id]',
    examples: ['/mail manage', '/mail manage example.com'],
    requiresOperand: true,
  },
  {
    name: 'ftp',
    description: 'Manage FTP users',
    usage: '/ftp manage [domain-or-id]',
    examples: ['/ftp manage', '/ftp manage example.com'],
    requiresOperand: true,
  },
  {
    name: 'vhost',
    description: 'Manage vhost settings',
    usage: '/vhost manage [domain-or-id]',
    examples: ['/vhost manage', '/vhost manage example.com'],
    requiresOperand: true,
  },
  {
    name: 'redis',
    description: 'Manage Redis cache',
    usage: '/redis manage [domain-or-id]',
    examples: ['/redis manage', '/redis manage example.com'],
    requiresOperand: true,
  },
  {
    name: 'rerun',
    description: 'Re-run the previous non-meta command',
    usage: '/rerun',
    examples: ['/rerun'],
  },
  {
    name: 'clear',
    description: 'Clear the console transcript',
    usage: '/clear',
    examples: ['/clear'],
  },
  {
    name: 'history',
    description: 'Show recent commands from this browser',
    usage: '/history',
    examples: ['/history'],
  },
];

/** Subcommands that complete an action command into an actionId. */
export const CONSOLE_ACTION_SUBCOMMANDS: { [command: string]: string[] } = {
  staging: ['create', 'merge'],
  alias: ['create'],
  subdomain: ['create'],
  wp: ['install', 'uninstall'],
  dns: ['manage'],
  ssl: ['manage'],
  db: ['manage'],
  mail: ['manage'],
  ftp: ['manage'],
  vhost: ['manage'],
  redis: ['manage'],
};

export function getHelpCommands(): ConsoleCommandDefinition[] {
  return CONSOLE_COMMANDS.filter((cmd) => !cmd.hidden);
}

export function getConsoleCommand(name: string): ConsoleCommandDefinition | undefined {
  const normalized = name.replace(/^\//, '').toLowerCase();
  return CONSOLE_COMMANDS.find((cmd) => cmd.name === normalized);
}

export function isConsoleCommandName(value: string): value is ConsoleCommandName {
  return CONSOLE_COMMANDS.some((cmd) => cmd.name === value);
}

export function isMetaConsoleCommand(command: string): boolean {
  const name = command.trim().replace(/^\//, '').split(/\s+/)[0].toLowerCase();
  return CONSOLE_META_COMMANDS.indexOf(name) !== -1;
}

export function isOpenTarget(value: string): value is 'server' | 'group' | 'unix' {
  return (CONSOLE_OPEN_TARGETS as readonly string[]).indexOf(value.toLowerCase()) !== -1;
}
